import {
  DiscoverySubItemSerializer,
  PublicDiscoveryViewSerializer,
} from "@app-contexts/public/features/discovery/data/types/discovery";
import { getDiscoveryItemPath } from "@app-contexts/public/features/discovery/data/utils/discovery-utils";
import EventStatusBadge from "@app-contexts/public/features/event-detail/components/EventStatusBadge/EventStatusBadge.web";
import { CityLandingPageEventSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import { enums } from "@shared/constants";
import Track from "@shared/modules/analytics/components/Track/Track.web";
import {
  PublicBaseEventSerializer,
  TicketTypeSerializer,
} from "@shared/modules/events/types/public";
import {
  eventHasWaitlistItems,
  getDynamicEventDateRange,
  isMultiDayEvent,
  isSoldOutEvent,
} from "@shared/modules/events/utils/event-utils";
import {
  RecommendedRating,
  PublicRatingSerializer,
} from "@shared/modules/ratings/types/ratings";
import { getRecommendedRating } from "@shared/modules/ratings/utils/ratings";
import { getPriceRange } from "@shared/modules/financials/utils/price-utils";
import MembershipRenewalText from "@shared/modules/memberships/components/MembershipRenewalText/MembershipRenewalText.web";
import breakpoints from "@shared/theme/props/breakpoints";
import { formatDate } from "@shared/utils/dates/formatting";
import { getEventImageSource } from "@shared/utils/images/utils";
import InventoryBadge from "@ui-application/data-display/badges/InventoryBadge/InventoryBadge.web";
import CardImage from "@ui-application/media/CardImage/CardImage.web";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import Link from "@ui-kit/navigation/Link/Link.web";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import { WAFFLE_FLAG_RECOMMENDED_BADGE } from "@shared/constants/waffle-flags";
import { useMemo } from "react";

const { xs, sm } = breakpoints;

// Helper function to get rating from different event types
const getEventRating = (
  event:
    | PublicBaseEventSerializer
    | CityLandingPageEventSerializer
    | PublicDiscoveryViewSerializer
): PublicRatingSerializer | undefined => {
  // Check if it's a PublicDiscoveryViewSerializer (has rating fields at root level)
  if (
    "average_rating" in event &&
    "total_ratings" in event &&
    "ratings_distribution" in event
  ) {
    // Convert ratings_distribution to distribution for PublicRatingSerializer
    return {
      average_rating: event.average_rating || "0.0",
      total_ratings: event.total_ratings || 0,
      distribution: event.ratings_distribution || {},
    };
  }

  // For PublicBaseEventSerializer and CityLandingPageEventSerializer, use venue.rating
  return event.venue?.rating;
};

export type EventDetailsCardProps = {
  isLoading?: boolean;
  event:
    | PublicBaseEventSerializer
    | CityLandingPageEventSerializer
    | PublicDiscoveryViewSerializer
    | undefined;
  onClick?: () => void;
  tracking_label_suffix?: string;
  hideFavoriteIcon?: boolean;
  allInPricing?: boolean;
};

export const EventDetailsCard: React.FC<EventDetailsCardProps> = ({
  isLoading,
  event,
  onClick,
  tracking_label_suffix,
  hideFavoriteIcon = false,
  allInPricing = false,
}) => {
  const { t } = useTranslation("common");
  const { isFlagActive } = useDjangoWaffle();

  let renewalObj = null;

  // convert any fields to a common name for use inside of display.
  let subItems:
    | TicketTypeSerializer[]
    | DiscoverySubItemSerializer[]
    | undefined;
  let no_sub_items: boolean | undefined = false;

  if (event !== null && event !== undefined) {
    if ("id" in event) {
      subItems = event.ticket_types;
      no_sub_items = event.no_ticket_types;
    } else if ("item_id" in event) {
      subItems = event.sub_items;
      no_sub_items = event.no_sub_items;
      renewalObj = enums.RENEWAL_FREQUENCIES_MAP_BY_LIST.find(
        (renewalItem) => renewalItem.id === event.renewal_frequency
      );
    }
  }

  const prices = no_sub_items
    ? t("Free")
    : getPriceRange({
        items: subItems,
        allInPricing:
          Boolean(event?.venue?.all_in_pricing_is_enabled) || allInPricing,
        venueCurrency: event?.venue?.currency,
      });

  const getEventID = (
    event:
      | PublicBaseEventSerializer
      | CityLandingPageEventSerializer
      | PublicDiscoveryViewSerializer
      | undefined
  ) => {
    if (!event) {
      return undefined;
    }
    if ("id" in event) {
      return event.id;
    }

    return event.item_id;
  };

  const getVenueID = (
    event:
      | PublicBaseEventSerializer
      | CityLandingPageEventSerializer
      | PublicDiscoveryViewSerializer
      | undefined
  ) => {
    if (!event) {
      return undefined;
    }

    if ("venue_id" in event) {
      return event.venue_id;
    }

    if ("venue" in event) {
      return event?.venue?.id;
    }

    return undefined;
  };

  const eventItemType = enums.DISCOVERY_VIEW_TYPE_MAP.event.id;
  const handleClick = onClick
    ? (e: React.MouseEvent) => {
        e.preventDefault();
        onClick();
      }
    : undefined;
  const href = getDiscoveryItemPath(event);

  const isRecommendedBadgeEnabled = isFlagActive(
    WAFFLE_FLAG_RECOMMENDED_BADGE,
    getVenueID(event)
  );

  // Calculate the recommended rating for the event based on customer reviews.
  // When customer reviews are disabled, isRecommended is set to false, preventing the rating from being displayed.
  const recommendedRating: RecommendedRating = useMemo(() => {
    if (event && isRecommendedBadgeEnabled) {
      return getRecommendedRating(getEventRating(event));
    }

    return { isRecommended: false, recommendedPercentage: 0 };
  }, [event, isRecommendedBadgeEnabled]);

  const showTime = !!event && event.starts_on;

  return (
    <Track.Click
      analytics={{
        label: `event-details-card-${tracking_label_suffix}`,
        event_id: getEventID(event),
        venue_id: getVenueID(event),
        custom_params: {
          is_sponsored: event?.is_sponsored,
          item_type: eventItemType,
          item_id: getEventID(event),
        },
      }}
    >
      <Link
        href={href}
        onClick={handleClick}
        data-testid="event-details-card"
        role="button"
        tabIndex={0}
      >
        {/* Container */}
        <Flex
          tabIndex={0}
          bg="bg"
          direction="column"
          cursor={isLoading ? "default" : "pointer"}
          borderRadius="md"
          border="1px solid"
          borderColor="gray.400"
          overflow="hidden"
          width="100%"
          height="100%"
        >
          <Skeleton isLoaded={!isLoading}>
            <CardImage
              favoriteData={!hideFavoriteIcon ? { event } : undefined}
              src={getEventImageSource(event)}
              alt={t("Event image for {{eventName}}", {
                eventName: event?.name,
              })}
              title={t("Event image for {{eventName}}", {
                eventName: event?.name,
              })}
              ariaLabel={t("Event image for {{eventName}}", {
                eventName: event?.name,
              })}
              height={600}
              width={1200}
              sizes={`(max-width: ${xs}px) ${xs}px, (max-width: ${sm}px) ${sm}px, (min-width: ${sm}px) 400px, 1200px`}
            />
          </Skeleton>

          {/* ContentContainer */}
          <Flex
            flexGrow={2}
            px="xs"
            py="sm"
            position="relative"
            height="auto"
            justifyContent="space-between"
            direction="column"
          >
            <Flex direction="column" width="100%" alignContent="flex-start">
              {/* DateStatusWrapper */}
              <Flex
                alignItems="center"
                justifyContent="space-between"
                height={{
                  base: "element.xl",
                  sm: "element.sm",
                  lg: "element.md",
                }}
              >
                <Skeleton isLoaded={!isLoading}>
                  {!!event && event.starts_on && event.ends_on && (
                    <Text
                      fontSize={{ base: "lg", sm: "xs", lg: "md" }}
                      lineHeight={{ base: "22px", sm: "14px", lg: "21px" }}
                      color="primary.800"
                    >
                      {getDynamicEventDateRange(event, t("Date & Time TBD"))}
                    </Text>
                  )}
                  {!!event &&
                    !event.starts_on &&
                    !event.ends_on &&
                    renewalObj && (
                      <Text
                        fontSize={{ base: "lg", sm: "xs", lg: "md" }}
                        lineHeight={{ base: "22px", sm: "14px", lg: "21px" }}
                        color="primary.800"
                      >
                        <MembershipRenewalText
                          renewalFrequency={renewalObj?.id}
                          as="span"
                          fontSize="inherit"
                          noOfLines={1}
                        />
                      </Text>
                    )}
                </Skeleton>

                {/* StatusContainer */}
                <Box
                  position="absolute"
                  zIndex={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  top="-12px"
                >
                  {!!event && (
                    <EventStatusBadge
                      eventStatus={event.status}
                      soldOut={isSoldOutEvent(event)}
                      waitlisted={eventHasWaitlistItems(event)}
                      recurringData={{
                        isRecurring: Boolean(
                          event.is_recurring_parent &&
                            event.child_count &&
                            event.child_count > 1
                        ),
                        isMultiDay: isMultiDayEvent(event),
                      }}
                      recommendedRating={recommendedRating}
                    />
                  )}
                </Box>
              </Flex>

              <Skeleton isLoaded={!isLoading}>
                {/* EventNameText */}
                <Text
                  mt="md"
                  fontWeight="bold"
                  fontSize={{ base: "md", sm: "sm", lg: "md" }}
                  lineHeight={{ base: "19px", sm: "17px", lg: "19px" }}
                  color="fg"
                  lineClamp={2}
                >
                  {event?.name}
                </Text>
              </Skeleton>

              <Skeleton width="fit-content" isLoaded={!isLoading}>
                {/* CityText */}
                <Text
                  mt="xs"
                  fontSize={{ base: "sm", sm: "xs", lg: "sm" }}
                  lineHeight={{ base: "17px", sm: "14px", lg: "17px" }}
                  color="fg"
                >
                  {event?.location?.city || t("Location Unavailable")}
                </Text>
              </Skeleton>
            </Flex>

            <Flex direction="column" width="100%" alignContent="flex-end">
              <Box minHeight="element.md" />
              <Skeleton isLoaded={!isLoading}>
                <Box minHeight="element.xl">
                  {/* Display low inventory or remaining tickets badge */}
                  {event && event.venue && (
                    <InventoryBadge eventData={event} venueData={event.venue} />
                  )}
                </Box>
              </Skeleton>
              <Skeleton isLoaded={!isLoading}>
                <Flex fontSize="sm" lineHeight="18px">
                  {prices && (
                    <Text
                      as="span"
                      display="inline-block"
                      pr={{ base: "xs", xl: "md" }}
                      borderRight={showTime ? "1px solid" : "none"}
                      borderColor="fg"
                    >
                      {prices}
                    </Text>
                  )}
                  {showTime && (
                    <Text
                      as="span"
                      display="inline-block"
                      pl={{ base: "xs", xl: "md" }}
                    >
                      {event?.starts_on &&
                        formatDate({
                          date: event?.starts_on,
                          timezone: event?.timezone,
                          format: "h:mm a zzz",
                        })}
                    </Text>
                  )}
                </Flex>
              </Skeleton>
            </Flex>
          </Flex>
        </Flex>
      </Link>
    </Track.Click>
  );
};
